// useEntityCommentsDrawer — lets a list open the reusable EntityCommentsDrawer from a ROW action, without
// opening the edit drawer (roadmap #6). The list calls `open({ entityKind, entityId, title })` from the
// Comments row/swipe action and renders `{drawer}` once. Keeps the drawer state + a single mounted instance
// out of every row. Returns null drawer when there's no event or nothing is open.
import { useCallback, useState, type ReactElement } from "react";

import { useDrawerMount } from "@/hooks/useDrawerMount";

import { EntityCommentsDrawer } from "./EntityCommentsDrawer";

export interface EntityCommentsTarget {
  /** The item's own event id (items in a list all share it, but each carries it). */
  eventId: string;
  entityKind: string;
  entityId: string;
  title?: string;
}

export interface UseEntityCommentsDrawerReturn {
  /** Open the comments drawer for one item. */
  open: (target: EntityCommentsTarget) => void;
  /** The mounted drawer (or null). Render this once in the list. */
  drawer: ReactElement | null;
}

export function useEntityCommentsDrawer(): UseEntityCommentsDrawerReturn {
  const [target, setTarget] = useState<EntityCommentsTarget | null>(null);

  const open = useCallback((next: EntityCommentsTarget) => {
    setTarget(next);
  }, []);

  // Mounted while a target is set (and while it slides out), opened one commit after it mounted — so it slides in.
  const mount = useDrawerMount(target && target.eventId && target.entityId ? target : null);
  const shown = mount.value;

  const drawer =
    mount.mounted && shown ? (
      <EntityCommentsDrawer
        opened={mount.opened}
        onClose={() => setTarget(null)}
        eventId={shown.eventId}
        entityKind={shown.entityKind}
        entityId={shown.entityId}
        title={shown.title}
      />
    ) : null;

  return { open, drawer };
}
